import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import axios from "axios";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Endpoints } from "../../../Global/Endpoints";
import { useTranslation } from 'react-i18next';
import CustomDropdown from "../../../CustomControls/dropdownnn";

Modal.setAppElement("#root");

const VitalsModal = ({ isOpen, onClose }) => {  const { t, i18n } = useTranslation();

  const [selectedDate, setSelectedDate] = useState(null);
  const [bloodPressure, setBloodPressure] = useState("");
  const [glucose, setGlucose] = useState("");
  const [weight, setWeight] = useState("");
  const [heartRate, setHeartRate] = useState("");
  const [sleep, setSleep] = useState("");
  const [temperature, setTemperature] = useState("");
  const [unit, setUnit] = useState("kg");
  const [errors, setErrors] = useState({});
  const token = localStorage.getItem("AccessToken");
  const userData = useSelector((state) => state.userData);
  const deviceType = "PatientPortal";
  const emailId = useSelector((state) => state.email.email);
  
  // Reset fields and errors
  const resetFields = () => {
    setSelectedDate(null);
    setBloodPressure("");
    setGlucose("");
    setWeight("");
    setHeartRate("");
    setSleep("");
    setTemperature("");
    setUnit("kg");
    setErrors({});
    
  };
  const [pregnancyConsent, setPregnancyConsent] = useState("N");
const [isSubmittingConsent, setIsSubmittingConsent] = useState(false);
const [disableConsentToggle, setDisableConsentToggle] = useState(false);


  // Effect to reset fields whenever the modal is opened
  useEffect(() => {
    if (isOpen) {
      resetFields();
      if (disableConsentToggle) {
        setPregnancyConsent("Y");
      } else {
        setPregnancyConsent("N");
      }
    }
  }, [isOpen]);
  // Validate all fields
const validateFields = () => {
    const newErrors = {};

    // Validate the date field
    if (!selectedDate) newErrors.selectedDate = "Please select a date";    // Check if at least one of these fields is filled
    const isAnyVitalEntered = bloodPressure || glucose || weight || heartRate || sleep || temperature;

    if (!isAnyVitalEntered) {
      newErrors.generalVitals = t("atleastoneVitalRequiredText");  // Show single message above the button
  } else {
      // Validate blood pressure format (only if entered)
      if (bloodPressure && !/^[1-9]\d{1,2}\/[1-9]\d{1,2}$/.test(bloodPressure)) {
        newErrors.bloodPressure = "Please enter blood pressure in format (e.g., 120/80)";
      }      // Heart rate validation (30-220)
      if (heartRate) {
        const heartRateNum = parseInt(heartRate);
        if (isNaN(heartRateNum) || heartRateNum < 30 || heartRateNum > 220) {
          newErrors.heartRate = "Heart rate must be between 30 and 220";
        }
      }

      // Temperature validation (30.0-45.0)
      if (temperature) {
        if (!/^\d+\.\d{2}$/.test(temperature)) {
          newErrors.temperature = "Temperature must have exactly 2 decimal places (e.g., 37.50)";
        } else {
          const tempNum = parseFloat(temperature);
          if (tempNum < 30.0 || tempNum > 45.0) {
            newErrors.temperature = "Temperature must be between 30.00°C and 45.00°C";
          }
        }
      }      // Sleep validation not needed as it's now a dropdown with valid values
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
};

const handleConsentToggle = () => {
  //UPDATED: Only update local toggle state, no API call here
  const newStatus = pregnancyConsent === "Y" ? "N" : "Y";
  setPregnancyConsent(newStatus);
};


  const handleSave = async () => {
    if (!validateFields()) return; // Prevent saving if there are errors
    
    const payload = {
      Weight: weight === "" ? 0 : parseFloat(weight),
      Glucose: glucose === "" ? "" : parseFloat(glucose),
      SelectedDate: selectedDate ? selectedDate.toLocaleDateString("en-US") : "",
      TypeofWeight: unit,
      AppUserID: userData.userData.AppUserId,
      BloodPressure: bloodPressure,
      HeartRate: heartRate === "" ? 0 : parseInt(heartRate),
      Sleep: sleep === "" ? 0 : parseInt(sleep),
      Temperature: temperature === "" ? 0 : parseFloat(temperature),
      DeviceType: deviceType,
      EmailId: emailId
    };
    try {
      const response = await axios.post(
        Endpoints.SavePatientVitals,
        `_Params=${encodeURIComponent(JSON.stringify(payload))}`,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.status === 200) {
        if (response.data === "Details saved successfully") {
          // NEW: Call pregnancy consent API before closing modal
          if (pregnancyConsent === "Y") {
            const consentParams = {
              EmailID: emailId,
              Status: "Y",
              Contract: userData?.userData?.ContractIdentified || "",
            };
  
            try {
              const consentResponse = await axios.post(
               Endpoints.UpdatePregnancyDataConsent,
                `_Params=${encodeURIComponent(JSON.stringify(consentParams))}`,
                {
                  headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    Authorization: `Bearer ${token}`,
                  },
                }
              );
  
              if (consentResponse.status === 200) {
                //toast.success(t("consentUpdatedSuccessfully"));
                setDisableConsentToggle(false); 
              } else {
                toast.error(t("errorUpdatingConsent"));
              }
            } catch (error) {
              console.error("Error updating consent:", error);
              toast.error(t("errorUpdatingConsent"));
            }
          }
  
          toast.success(t("vitalsavemessage"));
          onClose(); 
        } else if (response.data === "Unable to save data") {
          toast.error("Unable to save data");
        } else {
          toast.error(t("errorvitalmessage"));
        }
      } else {
        toast.error(t("errorvitalmessage"));
      }
    } catch (error) {
      toast.error(t("errorvitalmessage"));
    }
}

  const handleNumericInput = (setter) => (e) => {
    const value = e.target.value;
    if (/^\d*\.?\d*$/.test(value)) {
      setter(value);
      setErrors((prevErrors) => ({ ...prevErrors, [e.target.name]: "" }));
    }
  };  

  const handleBloodPressureInput = (setter) => (e) => {
    const value = e.target.value;

    // Always update the state with the current input value
    setter(value);

    // Strict validation for "120/70" format (both systolic and diastolic required)
    if (/^[1-9]\d{1,2}\/[1-9]\d{1,2}$/.test(value)){
      // Clear error if the format is valid
      setErrors((prevErrors) => ({ ...prevErrors, [e.target.name]: "" }));
    } else {
      // Set error for invalid format
      setErrors((prevErrors) => ({
        ...prevErrors,
        [e.target.name]: t("invalidformate"),
      }));
    }
  };

  const handleHeartRateInput = (e) => {
    const value = e.target.value;
    // Allow only numbers
    if (/^\d*$/.test(value)) {
      setHeartRate(value);
      const heartRateNum = parseInt(value);
      if (!value) {
        setErrors((prevErrors) => ({ ...prevErrors, heartRate: "" }));
      } else if (!isNaN(heartRateNum)) {
        if (heartRateNum >= 30 && heartRateNum <= 220) {
          setErrors((prevErrors) => ({ ...prevErrors, heartRate: "" }));
        } else {          setErrors((prevErrors) => ({
            ...prevErrors,
            heartRate: "Heart rate must be between 30 and 220"
          }));
        }
      }
    }
  };  const handleTemperatureInput = (e) => {
    const value = e.target.value;
    // Allow only numbers and decimal point
    if (/^\d*\.?\d*$/.test(value)) {
      setTemperature(value);

      // Clear error if empty
      if (!value) {
        setErrors((prevErrors) => ({ ...prevErrors, temperature: "" }));
        return;
      }

      // Check format and range in sequence
      if (!/^\d+\.\d{2}$/.test(value)) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          temperature: "Temperature must have exactly 2 decimal places (e.g., 37.50)"
        }));
      } else {
        const tempNum = parseFloat(value);
        if (tempNum >= 30.0 && tempNum <= 45.0) {
          setErrors((prevErrors) => ({ ...prevErrors, temperature: "" }));
        } else {
          setErrors((prevErrors) => ({
            ...prevErrors,
            temperature: "Temperature must be between 30.00°C and 45.00°C"
          }));
        }
      }
    }
  };
  const handleCancel = () => {
    resetFields();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Vitals Entry"
      className="tracker-modal-content-v"
      overlayClassName="tracker-modal-overlay"
      shouldCloseOnOverlayClick={false}
    >
      <h2>{t("vitalheading")}</h2>
      <div className="modelll-form-wrapper">
        <div className="mb-4 row align-items-center">
          <label className="col-sm-4 model-form-label">{t("labselectdatevital")}</label>
          <div className="col-sm-8">
            <DatePicker
              selected={selectedDate}
              onChange={(date) => {
                setSelectedDate(date);
                setErrors((prevErrors) => ({ ...prevErrors, selectedDate: "" }));
              }}
              placeholderText="MM/DD/YYYY"
              onKeyDown={(e) => e.preventDefault()}
              maxDate={new Date()}
              autoComplete="off"
              className="form-control win-form-control calendar-control mobile-view"
            />
            {errors.selectedDate && (
              <div className="form-field-error-message">{errors.selectedDate}</div>
            )}
          </div>
        </div>

        <div className="mb-4 row align-items-center">
          <label className="col-sm-4 model-form-label">{t("BloodPressurevital")}</label>
          <div className="col-sm-8">
            <input
              type="text"
              name="bloodPressure"
              value={bloodPressure}
              onChange={handleBloodPressureInput(setBloodPressure)}
              placeholder={t("Blood Pressure")}
              autoComplete="off"
              maxLength={7}
              className="form-control win-form-control mobile-view"
            />
            {errors.bloodPressure && <div className="form-field-error-message">{errors.bloodPressure}</div>}
          </div>
        </div>

        <div className="mb-4 row align-items-center">
          <label className="col-sm-4 model-form-label">{t("Glucose")}</label>
          <div className="col-sm-8">
            <input
              type="text"
              name="glucose"
              value={glucose}
              onChange={handleNumericInput(setGlucose)}
              placeholder={t("Glucoseplaceholder")}
              autoComplete="off"
              maxLength={3}
              className="form-control win-form-control mobile-view"
            />
           
          </div>
        </div>

       

        <div className="mb-4 row align-items-center">
          <label className="col-sm-4 model-form-label">{t("Heart Rate")}</label>
          <div className="col-sm-8">
            <input
              type="text"
              name="heartRate"
              value={heartRate}
              onChange={handleHeartRateInput}
              placeholder="Heart Rate"
              autoComplete="off"
              maxLength={3}
              className="form-control win-form-control mobile-view"
            />
            {errors.heartRate && <div className="form-field-error-message">{errors.heartRate}</div>}
          </div>
        </div>        <div className="mb-4 row align-items-center">
          <label className="col-sm-4 model-form-label">{t("Sleep")}</label>
          <div className="col-sm-8">            <CustomDropdown
              options={Array.from({ length: 16 }, (_, i) => i.toString())}
              selectedOption={sleep || ""}
              onChange={(option) => {
                setSleep(option);
                setErrors((prevErrors) => ({ ...prevErrors, sleep: "" }));
              }}
              placeholder={t("Select sleep hours")}
              searchable={true}
            />
            {errors.sleep && <div className="form-field-error-message">{errors.sleep}</div>}
          </div>
        </div>

        <div className="mb-4 row align-items-center">
          <label className="col-sm-4 model-form-label">{t("Temperature")}</label>
          <div className="col-sm-8">
            <input
              type="text"
              name="temperature"
              value={temperature}
              onChange={handleTemperatureInput}
              placeholder="Temperature"
              autoComplete="off"
              maxLength={5}
              className="form-control win-form-control mobile-view"
            />
            {errors.temperature && <div className="form-field-error-message">{errors.temperature}</div>}
          </div>
        </div>

       

         <div className="mb-4 row align-items-center">
          <label className="col-sm-4 model-form-label">{t("Weight")}</label>
          <div className="col-sm-8">
            <input
              type="text"
              name="weight"
              value={weight}
              onChange={handleNumericInput(setWeight)}
              placeholder={t("Weightplaceholder")}
              autoComplete="off"
              maxLength={3}
              className="form-control win-form-control mobile-view"
            />
           
          </div>
      
        </div>
        <div className="mb-4 row align-items-center">
          <label className="col-sm-4 model-form-label"></label>
          <div className="col-sm-8 d-flex">
            <button
              className={unit === "kg" ? "unit-button me-2 active" : "unit-button me-2"}
              onClick={() => setUnit("kg")}
            >
              {t(("kg"))}
            </button>
            <button
              className={unit === "lbs" ? "unit-button active" : "unit-button"}
              onClick={() => setUnit("lbs")}
            >
              {t(("lbs"))}
            </button>
          </div>
        </div>
                {errors.generalVitals && (
          <>
            <div className="mb-4 row">
              <label className="col-sm-4 model-form-label">&nbsp;</label>
              <div className="col-sm-7 form-field-error-message text-danger mt-2">
                {errors.generalVitals}
              </div>
            </div>
          </>
        )}
        <div className="mb-4 row align-items-center">
        <label className="col-sm-4 model-form-label"></label>
  <div className="col-sm-12">
    <label className="switch">
      <input
        type="checkbox"
        className="switch-input"
        checked={pregnancyConsent === "Y"}
        onChange={handleConsentToggle}
        disabled={disableConsentToggle}
      />
      <span className="slider round"></span>
    </label>
    <span className="modelswitch-label ms-4">{t('updateconsent')}</span>
  </div>
        </div>
     
        <div className="mb-4 row align-items-center">
          <label className="col-sm-4 model-form-label"></label>
          <div className="col-sm-12 d-flex justify-content-end">
            <button
              className="win-btn-secondary model-popup-btn me-2 mb-3 mb-sm-0"
              onClick={handleCancel}
            >
              {t("Cancel")}
            </button>
            <button
              className="win-btn-primary model-popup-btn me-3"
              onClick={handleSave}
            >
               {t("Save")}
            </button>
          </div>
        </div>
        
      </div>
    </Modal>
  );
};

export default VitalsModal;
